import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import ExtensionService from '../service';
import ContextService from '../../Context/service';
import CostCenterService from '../service/centr_de_custo';
import PJSIPConfiguration from '../service/pjsip_configuration';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useHistory, useLocation } from 'react-router-dom';
import ReactButton from 'components/Buttons/ReactButton';

import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody,
  FormGroup,
  Label
} from 'reactstrap';
import { ScreenPrompt } from 'components/ScreenPrompt';
import { verifyRoles } from 'components/Authentication/VerifyRoles';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';
import useExtensions from 'hooks/useExtensions';
import useRingGroups from 'hooks/useRingGroups';
import useAudios from 'hooks/useAudios';
import useQueues from 'hooks/useQueues';

const overflowTypeOptions = [
  { label: 'Fila', value: 'QUEUE' },
  { label: 'Ramal', value: 'EXTENSION' },
  { label: 'Desligar', value: 'HANGUP' },
  { label: 'Grupo de Toque', value: 'RING_GROUP' }
];

const matchInputStylesForSelect = {
  control: (base, state) => ({
    ...base,
    color: '#8898aa',
    borderColor: state.isFocused ? '#5e72e4' : '#cad1d7',
    boxShadow: state.isFocused ? '0 0 0 1px #5e72e4' : 'none',
    '&:hover': { borderColor: '#5e72e4' },
    fontSize: '0.875rem'
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 8px'
  }),
  input: (base) => ({ ...base, margin: 0 }),
  indicatorsContainer: (base) => ({
    ...base
  }),
  placeholder: (base) => ({
    ...base,
    color: '#8898aa'
  })
};

const FormPjsip = (props) => {
  const history = useHistory();
  const location = useLocation();
  const { type, actionType } = location.state;
  const { id } = props.match.params;

  const { reset, control, handleSubmit, errors, formState, watch } = useForm();
  const { isDirty } = formState;
  const [isUpdate, setIsUpdate] = useState(false);
  const [isExtensionRange, setIsExtensionRange] = useState(false);

  const [contexts, setContexts] = useState([]);
  const [costsCenter, setCostsCenter] = useState([]);
  const [pjsipConfiguration, setPjsipConfiguration] = useState([]);

  const { options: extensionOptions } = useExtensions();
  const { options: queueOptions } = useQueues({ types: ['is_telephony'] });
  const { options: ringGroupOptions } = useRingGroups();
  const { groupedByType } = useAudios();

  const isOutsideHoursEnabled = watch('outside_hours_enabled');
  const outsideHoursType = watch('outside_hours_type');
  const isUnavailableEnabled = watch('unavailable_enabled');
  const unavailableType = watch('unavailable_type');

  const outsideHoursDestinationOptions = useMemo(() => {
    if (outsideHoursType?.value === 'QUEUE') {
      return queueOptions ?? [];
    } else if (outsideHoursType?.value === 'EXTENSION') {
      return extensionOptions ?? [];
    } else if (outsideHoursType?.value === 'RING_GROUP') {
      return ringGroupOptions ?? [];
    } else if (outsideHoursType?.value === 'HANGUP') {
      return (groupedByType.sound || []).map((audio) => ({
        label: audio.name,
        value: String(audio.id)
      }));
    }
    return [];
  }, [
    outsideHoursType,
    queueOptions,
    extensionOptions,
    ringGroupOptions,
    groupedByType.sound
  ]);

  const unavailableDestinationOptions = useMemo(() => {
    if (unavailableType?.value === 'QUEUE') {
      return queueOptions ?? [];
    } else if (unavailableType?.value === 'EXTENSION') {
      return extensionOptions ?? [];
    } else if (unavailableType?.value === 'RING_GROUP') {
      return ringGroupOptions ?? [];
    } else if (unavailableType?.value === 'HANGUP') {
      return (groupedByType.sound || []).map((audio) => ({
        label: audio.name,
        value: String(audio.id)
      }));
    }
    return [];
  }, [
    unavailableType,
    queueOptions,
    extensionOptions,
    ringGroupOptions,
    groupedByType.sound
  ]);

  // Get Contexts to select
  useEffect(() => {
    ContextService.getAll()
      .then((response) => {
        setContexts(response.data.data);
      })
      .catch(() => {
        console.log('Erro ao ler os contextos');
      });
  }, []);

  // Get costs center to select
  useEffect(() => {
    CostCenterService.getAll()
      .then((response) => {
        setCostsCenter(response.data.data);
      })
      .catch(() => {
        console.log('Erro ao ler os centros de custo');
      });
  }, []);

  // Get PJSIP configurations to select
  useEffect(() => {
    if (type === 'PJSIP') {
      PJSIPConfiguration.getAll()
        .then((response) => {
          setPjsipConfiguration(response.data.data);
        })
        .catch(() => {
          console.log('Erro ao ler configurações do PJSIP');
        });
    }
  }, [type]);

  /**
   * Função auxiliar para encontrar o objeto de opção completo a partir de um valor.
   */
  const findOption = useCallback((value, options) => {
    if (value === null || value === undefined || !options?.length) {
      return null;
    }
    const searchValue = String(value);
    return options.find((opt) => String(opt.value) === searchValue) || null;
  }, []);

  // Load extension if is update
  useEffect(() => {
    const loadExtensionData = async () => {
      try {
        const response = await ExtensionService.get(id);
        const fetchedData = response.data.data;

        // Determina as opções de destino corretas com base no tipo
        const soundOptions = (groupedByType.sound || []).map((audio) => ({
          label: audio.name,
          value: String(audio.id)
        }));

        const initialOutsideHoursDestOptions =
          fetchedData.outside_hours_type === 'QUEUE'
            ? queueOptions
            : fetchedData.outside_hours_type === 'EXTENSION'
            ? extensionOptions
            : fetchedData.outside_hours_type === 'RING_GROUP'
            ? ringGroupOptions
            : fetchedData.outside_hours_type === 'HANGUP'
            ? soundOptions
            : [];

        const initialUnavailableDestOptions =
          fetchedData.unavailable_type === 'QUEUE'
            ? queueOptions
            : fetchedData.unavailable_type === 'EXTENSION'
            ? extensionOptions
            : fetchedData.unavailable_type === 'RING_GROUP'
            ? ringGroupOptions
            : fetchedData.unavailable_type === 'HANGUP'
            ? soundOptions
            : [];

        // Mapeia os dados de selects que não são hooks para o formato { label, value }
        const costCenterOptions = costsCenter.map((c) => ({
          value: c.id,
          label: c.name
        }));
        const contextOptions = contexts.map((c) => ({
          value: c.id,
          label: c.name
        }));
        const pjsipOptions = pjsipConfiguration.map((p) => ({
          value: p.id,
          label: p.name
        }));

        // Transforma os dados para o formato que o formulário espera
        const dataForReset = {
          ...fetchedData,
          cost_center: findOption(fetchedData.cost_center, costCenterOptions),
          context: findOption(fetchedData.context, contextOptions),
          pjsip_configuration_id: findOption(
            fetchedData.pjsip_configuration_id,
            pjsipOptions
          ),
          outside_hours_type: findOption(
            fetchedData.outside_hours_type,
            overflowTypeOptions
          ),
          unavailable_type: findOption(
            fetchedData.unavailable_type,
            overflowTypeOptions
          ),
          outside_hours_destination_id: findOption(
            fetchedData.outside_hours_destination_id,
            initialOutsideHoursDestOptions
          ),
          unavailable_destination_id: findOption(
            fetchedData.unavailable_destination_id,
            initialUnavailableDestOptions
          )
        };

        if (actionType === 'CLONE') {
          reset({
            ...dataForReset,
            extension_number: '',
            identifier: ''
          });
        } else {
          reset(dataForReset);
        }
        setIsUpdate(true);
      } catch (err) {
        toast.error('Erro ao tentar carregar ramal', {
          autoClose: 3000,
          closeOnClick: true
        });
      }
    };

    if (
      id &&
      costsCenter.length &&
      contexts.length &&
      (type !== 'PJSIP' || pjsipConfiguration.length)
    ) {
      loadExtensionData();
    }
  }, [
    id,
    type,
    actionType,
    reset,
    findOption,
    costsCenter,
    contexts,
    pjsipConfiguration,
    queueOptions,
    extensionOptions,
    ringGroupOptions,
    groupedByType.sound
  ]);

  const onSubmit = async (data) => {
    const extensionData = { ...data, type };

    extensionData.outside_hours_type = data.outside_hours_type?.value;
    extensionData.outside_hours_destination_id =
      data.outside_hours_destination_id?.value;
    extensionData.unavailable_type = data.unavailable_type?.value;
    extensionData.unavailable_destination_id =
      data.unavailable_destination_id?.value;

    extensionData.cost_center = data.cost_center?.value ?? data.cost_center;
    extensionData.context = data.context?.value ?? data.context;
    extensionData.pjsip_configuration_id = data.pjsip_configuration_id?.value;

    if (!data.identifier || String(data.identifier).length < 1)
      delete extensionData.identifier;

    try {
      const isClone = actionType === 'CLONE';

      if (!isUpdate || isClone) {
        isExtensionRange
          ? // Na criação de Range a senha deve ser mandada como { password }
            await ExtensionService.createExtensionRange({
              ...extensionData,
              password: extensionData.secret
            })
          : await ExtensionService.create(extensionData);
      } else {
        await ExtensionService.update(id, extensionData);
      }

      toast.success(
        isUpdate && !isClone ? 'Ramal atualizado' : 'Ramal cadastrado!',
        {
          autoClose: 3000,
          closeOnClick: true
        }
      );
      history.push('/admin/extensions');
    } catch (err) {
      console.log(err.response);
      toast.error('Erro ao salvar ramal', {
        autoClose: 3000,
        closeOnClick: true
      });
      history.push('/admin/extensions');
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/extensions');
  };

  const handleExtensionCreationType = () => {
    setIsExtensionRange(!isExtensionRange);
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/extensions"
        buttonTitle="Voltar para a pagina de ramais"
      />
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Ramal {type}</h3>
                </CardHeader>
                <CardBody>
                  {actionType === 'ADD' && (
                    <Row className="mb-3">
                      <Col sm="12">
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="radio"
                              name="isExtensionRange"
                              value="unique"
                              checked={!isExtensionRange}
                              onChange={handleExtensionCreationType}
                            />
                            Criar um ramal
                          </Label>
                        </FormGroup>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="radio"
                              name="isExtensionRange"
                              value="multi"
                              checked={isExtensionRange}
                              onChange={handleExtensionCreationType}
                            />
                            Criar intervalo de ramais
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  <Form
                    className="needs-validation"
                    onSubmit={handleSubmit(onSubmit)}
                  >
                    {!isExtensionRange && (
                      <Row>
                        <Col md={type === 'ANALOGICO' ? '12' : '6'}>
                          <label className="form-control-label">
                            Número do ramal*
                          </label>
                          <Controller
                            as={Input}
                            control={control}
                            name="extension_number"
                            rules={{
                              required: 'Campo obrigatório',
                              validate: (value) => {
                                if (String(value).length < 3)
                                  return 'No mínino 3 caracteres';
                                if (String(value).length > 10)
                                  return 'No máximo 10 caracteres';
                                return true;
                              }
                            }}
                            defaultValue=""
                            disabled={
                              verifyRoles(['EDIT_EXTENSIONS_RECORDINGS']) &&
                              !verifyRoles(['EXTENSIONS_EDIT'])
                            }
                          />
                          <ErrorMessage
                            errors={errors}
                            name="extension_number"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        {type !== 'ANALOGICO' && (
                          <Col md={{ size: 6 }}>
                            <label className="form-control-label">
                              Nome do ramal
                            </label>
                            <Controller
                              as={Input}
                              control={control}
                              name="identifier"
                              rules={{
                                validate: (value) => {
                                  if (String(value).length === 0) return true;
                                }
                              }}
                              defaultValue=""
                              disabled={
                                verifyRoles(['EDIT_EXTENSIONS_RECORDINGS']) &&
                                !verifyRoles(['EXTENSIONS_EDIT'])
                              }
                            />
                            <ErrorMessage
                              errors={errors}
                              name="identifier"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </Col>
                        )}
                      </Row>
                    )}
                    {verifyRoles(['EXTENSIONS_EDIT']) && (
                      <>
                        {isExtensionRange && (
                          <Row>
                            <Col md="6">
                              <label className="form-control-label">
                                Ramal Inicial*
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="initialExtension"
                                rules={{ required: 'Campo obrigatório' }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="initialExtension"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                            <Col md="6">
                              <label className="form-control-label">
                                Ramal final*
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="finalExtension"
                                defaultValue=""
                                rules={{ required: 'Campo obrigatório' }}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="finalExtension"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          </Row>
                        )}

                        <Row style={{ paddingTop: '15px' }}>
                          {type !== 'ANALOGICO' && (
                            <Col md="6">
                              <label className="form-control-label">
                                Senha*
                              </label>
                              <Controller
                                as={Input}
                                type="password"
                                control={control}
                                name="secret"
                                defaultValue=""
                                rules={{
                                  required: 'Defina uma senha',
                                  validate: (value) => {
                                    if (/^\S*$/.test(value)) return true;
                                    return 'Não utilize espaços em branco';
                                  }
                                }}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="secret"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          )}
                          {type === 'PJSIP' && (
                            <Col md="6">
                              <FormGroup>
                                <FlexControllerV2
                                  type="select"
                                  name="pjsip_configuration_id"
                                  control={control}
                                  label="Configuração*"
                                  options={pjsipConfiguration.map((p) => ({
                                    value: p.id,
                                    label: p.name
                                  }))}
                                  rules={{
                                    required: 'Selecione uma configuração'
                                  }}
                                  errors={errors}
                                  placeholder="Selecione uma configuração"
                                  styles={matchInputStylesForSelect}
                                />
                              </FormGroup>
                            </Col>
                          )}
                          {(type === 'SIP' || type === 'IAX2') && (
                            <Col md="6">
                              <label className="form-control-label">
                                Transporte*
                              </label>
                              <Controller
                                as={
                                  <Input type="select">
                                    <option value="">Selecione</option>
                                    <option value="UDP">UDP</option>
                                    <option value="TCP">TCP</option>
                                  </Input>
                                }
                                control={control}
                                name="transport"
                                rules={{ required: 'Selecione o transporte' }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="transport"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          )}
                        </Row>

                        <Row style={{ paddingTop: '15px' }}>
                          <Col md="6">
                            <FormGroup>
                              <FlexControllerV2
                                type="select"
                                name="cost_center"
                                control={control}
                                label="Setor*"
                                options={costsCenter.map((c) => ({
                                  value: c.id,
                                  label: c.name
                                }))}
                                rules={{ required: 'Campo obrigatório' }}
                                errors={errors}
                                placeholder="Selecione"
                                styles={matchInputStylesForSelect}
                              />
                            </FormGroup>
                          </Col>

                          <Col md="6">
                            <FormGroup>
                              <FlexControllerV2
                                type="select"
                                name="context"
                                control={control}
                                label="Contexto*"
                                options={contexts.map((c) => ({
                                  value: c.id,
                                  label: c.name
                                }))}
                                rules={{ required: 'Campo obrigatório' }}
                                errors={errors}
                                placeholder="Selecione"
                                styles={matchInputStylesForSelect}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <hr />
                        <h3>Configurações</h3>
                        {type !== 'ANALOGICO' && (
                          <Row>
                            <Col md="6">
                              <label className="form-control-label">
                                Call Group
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="callgroup"
                                rules={{
                                  validate: (value) => {
                                    if (
                                      /^([1-9]|[1-5][0-9]|6[0-3])?(,[1-9]|,[1-5][0-9]|,6[0-3]){0,4}$/.test(
                                        value
                                      )
                                    )
                                      return true;
                                    return 'Call Group inválido';
                                  }
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="callgroup"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                            <Col md="6">
                              <label className="form-control-label">
                                Pickup Group
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="pickupgroup"
                                rules={{
                                  validate: (value) => {
                                    if (
                                      /^([1-9]|[1-5][0-9]|6[0-3])?(,[1-9]|,[1-5][0-9]|,6[0-3]){0,4}$/.test(
                                        value
                                      )
                                    )
                                      return true;
                                    return 'Pickup Group inválido';
                                  }
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="pickupgroup"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          </Row>
                        )}

                        {type !== 'ANALOGICO' && (
                          <Row className="mb-2" style={{ paddingTop: '15px' }}>
                            <Col md="6">
                              <label className="form-control-label">
                                Limite de contatos*
                              </label>
                              <Controller
                                as={Input}
                                type="number"
                                control={control}
                                name="call_limit"
                                rules={{
                                  required: 'Campo obrigatório',
                                  validate: (value) => {
                                    return value > 0 && value < 100
                                      ? true
                                      : 'Limite entre 1 e 99';
                                  }
                                }}
                                defaultValue={1}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="call_limit"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>

                            <Col
                              className="mt-3 mt-md-4"
                              style={{ marginLeft: '1.5rem' }}
                            >
                              <Row>
                                {verifyRoles([
                                  'EDIT_EXTENSIONS_RECORDINGS'
                                ]) && (
                                  <>
                                    <Col sm="12">
                                      <Controller
                                        name="allow_external_call_recording"
                                        control={control}
                                        defaultValue={false}
                                        render={(props) => (
                                          <>
                                            <label className="form-control-label">
                                              <Input
                                                type="checkbox"
                                                onChange={(e) =>
                                                  props.onChange(
                                                    e.target.checked
                                                  )
                                                }
                                                checked={props.value}
                                              />
                                              Gravar chamadas externas
                                            </label>
                                          </>
                                        )}
                                      />
                                    </Col>
                                    <Col sm="12">
                                      <Controller
                                        name="allow_internal_call_recording"
                                        control={control}
                                        defaultValue={false}
                                        render={(props) => (
                                          <>
                                            <label className="form-control-label">
                                              <Input
                                                type="checkbox"
                                                onChange={(e) =>
                                                  props.onChange(
                                                    e.target.checked
                                                  )
                                                }
                                                checked={props.value}
                                              />
                                              Gravar chamadas internas
                                            </label>
                                          </>
                                        )}
                                      />
                                    </Col>
                                  </>
                                )}
                                <Col sm="12">
                                  <Controller
                                    name="allow_monitor_extension"
                                    control={control}
                                    defaultValue={false}
                                    render={(props) => (
                                      <>
                                        <label className="form-control-label">
                                          <Input
                                            type="checkbox"
                                            onChange={(e) =>
                                              props.onChange(e.target.checked)
                                            }
                                            checked={props.value}
                                          />
                                          Monitorar
                                        </label>
                                      </>
                                    )}
                                  />
                                </Col>
                                {type === 'PJSIP' && (
                                  <Col sm="12">
                                    <Controller
                                      name="nat"
                                      control={control}
                                      defaultValue={false}
                                      render={(props) => (
                                        <>
                                          <label className="form-control-label">
                                            <Input
                                              type="checkbox"
                                              onChange={(e) =>
                                                props.onChange(e.target.checked)
                                              }
                                              checked={props.value}
                                            />
                                            NAT
                                          </label>
                                        </>
                                      )}
                                    />
                                  </Col>
                                )}
                                {(type === 'SIP' || type === 'PJSIP') && (
                                  <Col sm="12">
                                    <Controller
                                      name="blf"
                                      control={control}
                                      defaultValue={false}
                                      render={(props) => (
                                        <>
                                          <label className="form-control-label">
                                            <Input
                                              type="checkbox"
                                              onChange={(e) =>
                                                props.onChange(e.target.checked)
                                              }
                                              checked={props.value}
                                            />
                                            BLF
                                          </label>
                                        </>
                                      )}
                                    />
                                  </Col>
                                )}
                              </Row>
                            </Col>
                          </Row>
                        )}

                        <Row className="mb-3">
                          <Col>
                            <h3 className="mb-1">Transbordo</h3>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md="4">
                            <FormGroup check>
                              <FlexControllerV2
                                type="checkbox"
                                name="outside_hours_enabled"
                                control={control}
                                label="Transbordar fora do horário"
                                errors={errors}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        {isOutsideHoursEnabled && (
                          <Row className="mt-1">
                            <Col md="6">
                              <FormGroup>
                                <FlexControllerV2
                                  type="select"
                                  name="outside_hours_type"
                                  control={control}
                                  label="Tipo de transbordo fora do horário*"
                                  options={overflowTypeOptions}
                                  placeholder="Selecione o tipo"
                                  errors={errors}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <FlexControllerV2
                                  type="select"
                                  name="outside_hours_destination_id"
                                  control={control}
                                  label="Destino do transbordo fora do horário*"
                                  options={outsideHoursDestinationOptions}
                                  placeholder="Selecione o destino"
                                  errors={errors}
                                  disabled={!outsideHoursType?.value}
                                  isClearable={
                                    outsideHoursType?.value === 'HANGUP'
                                  }
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        )}

                        <Row className="mt-3">
                          <Col md="4">
                            <FormGroup check>
                              <FlexControllerV2
                                type="checkbox"
                                name="unavailable_enabled"
                                control={control}
                                label="Transbordar caso indisponível"
                                errors={errors}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        {isUnavailableEnabled && (
                          <Row className="mt-1">
                            <Col md="6">
                              <FormGroup>
                                <FlexControllerV2
                                  type="select"
                                  name="unavailable_type"
                                  control={control}
                                  label="Tipo de transbordo caso indisponível*"
                                  options={overflowTypeOptions}
                                  placeholder="Selecione o tipo"
                                  errors={errors}
                                />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <FlexControllerV2
                                  type="select"
                                  name="unavailable_destination_id"
                                  control={control}
                                  label="Destino do transbordo caso indisponível*"
                                  options={unavailableDestinationOptions}
                                  placeholder="Selecione o destino"
                                  errors={errors}
                                  disabled={!unavailableType?.value}
                                  isClearable={
                                    unavailableType?.value === 'HANGUP'
                                  }
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        )}

                        <ReactButton btnColor="confirmation" type="submit">
                          Salvar
                        </ReactButton>
                        <ReactButton
                          btnColor="cancelation"
                          onClick={() => cancelAction()}
                        >
                          Cancelar
                        </ReactButton>
                      </>
                    )}
                  </Form>
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default FormPjsip;
